/**
 * Static sample output for the public tool landing pages.
 * These are illustrative mock-ups — never live data — and every surface that
 * renders one labels it as an example. Each panel deliberately replicates the
 * layout of the real screen inside that tool (same tabs, cards, badges, score
 * bands) so visitors see the product rather than a marketing illustration.
 */
import React from 'react';
import { Badge } from '@/components/ui/badge';
import { Sparkles, Copy, Repeat, Compass, Rocket, Route, MoonStar } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { ToolSlug } from '@/config/toolLandings';
import { AstrologyPreview } from '@/features/astrology/components/AstrologyPreview';
import { ChartWheel } from '@/features/astrology/components/ChartWheel';
import { computeChart } from '@/features/astrology/engine/chart';
import { signOf } from '@/features/astrology/engine/aspects';
import { SIGN_MEANINGS } from '@/features/astrology/data/constants';
import {
  AppShell,
  PanelTitle,
  MeterRow,
  FitRing,
  RadarMini,
  PosterCard,
  Callout,
  scoreBand,
} from './chrome';

/* ========================================================================== *
 * Career — mirrors CareerJobMatches / CareerResumeTailor / CareerChat
 * ========================================================================== */

const JobRow: React.FC<{
  title: string;
  company: string;
  score: number;
  blurb: string;
  reason: string;
}> = ({ title, company, score, blurb, reason }) => {
  const band = scoreBand(score);
  return (
    <div className="rounded-xl border border-border/60 bg-card p-3 space-y-2 transition-shadow hover:shadow-sm">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="truncate text-sm font-medium text-foreground">{title}</p>
          <p className="text-xs text-muted-foreground">{company}</p>
        </div>
        <span
          className={cn(
            'shrink-0 inline-flex items-center gap-0.5 rounded-full px-1.5 py-0.5 text-[10px]',
            band.bg,
            band.text,
          )}
        >
          <Sparkles className="h-3 w-3" aria-hidden />
          {score}%
        </span>
      </div>
      <p className="line-clamp-2 text-xs text-muted-foreground">{blurb}</p>
      <p className="text-xs italic text-info/90">{reason}</p>
      <span className="inline-flex items-center gap-1 text-[11px] text-muted-foreground">
        <Copy className="h-3 w-3" aria-hidden />
        Copy link
      </span>
    </div>
  );
};

export const CareerSample: React.FC = () => (
  <AppShell
    tabs={['Matches', 'Paths', 'Resume', 'Coach']}
    toolbar={
      <>
        <span>Product / research roles · remote</span>
        <span className="text-foreground">12 matches</span>
      </>
    }
  >
    <div className="space-y-3">
      <JobRow
        title="Senior Product Manager"
        company="Northlight Labs"
        score={91}
        blurb="Own discovery through launch for a research-led platform team; partner closely with design and data."
        reason="Reads people quickly and decides with incomplete information."
      />
      <JobRow
        title="UX Researcher II"
        company="Halden Health"
        score={84}
        blurb="Mixed-methods research across two product lines, with direct access to clinicians and patients."
        reason="High curiosity, comfortable sitting in ambiguity."
      />
      <JobRow
        title="Operations Lead"
        company="Corvid Logistics"
        score={62}
        blurb="Run weekly planning and vendor coordination for a 30-person fulfilment operation."
        reason="Fits the structure, less room for the exploration you prefer."
      />
    </div>
    <Callout tone="info" label="Watch for:">
      roles with long solo stretches and no feedback loop tend to drain you fastest.
    </Callout>
  </AppShell>
);

export const CareerResumeSample: React.FC = () => (
  <AppShell
    tabs={['Matches', 'Paths', 'Resume', 'Coach']}
    active={2}
    toolbar={
      <>
        <span>Senior Product Manager · Northlight Labs</span>
        <span className="text-success">Tailored</span>
      </>
    }
  >
    <div className="rounded-xl border border-border/60 bg-muted/25 p-3 space-y-2">
      <p className="text-[11px] uppercase tracking-widest text-muted-foreground">This post screens for</p>
      <div className="flex flex-wrap gap-1.5">
        {['Stakeholder management', 'Roadmap ownership', 'Data literacy'].map((t) => (
          <Badge key={t} variant="outline" className="border-info/40 text-info text-[10px]">
            {t}
          </Badge>
        ))}
      </div>
    </div>
    <div className="grid gap-2 sm:grid-cols-2">
      <div className="space-y-2">
        <p className="text-[11px] uppercase tracking-widest text-muted-foreground">Before</p>
        <div className="rounded-xl border border-border/60 bg-muted/20 p-3 opacity-70 space-y-1.5">
          <p className="text-xs leading-relaxed text-muted-foreground line-through">
            Assisted with internal documentation upkeep.
          </p>
          <p className="text-xs leading-relaxed text-muted-foreground">
            Worked with several teams on product initiatives.
          </p>
        </div>
      </div>
      <div className="space-y-2">
        <p className="text-[11px] uppercase tracking-widest text-info">After</p>
        <div className="rounded-xl border-y border-r border-l-2 border-border/60 border-l-info/60 bg-info/5 p-3 space-y-1.5">
          <p className="text-xs leading-relaxed text-foreground">
            Led weekly alignment across three teams, cutting rework by a third.
          </p>
          <p className="text-xs leading-relaxed text-foreground">
            Owned the discovery-to-launch roadmap for two shipped platform features.
          </p>
        </div>
      </div>
    </div>
    <p className="text-[11px] text-muted-foreground">
      Every rewrite keeps your facts — only ordering and emphasis change.
    </p>
  </AppShell>
);

export const CareerCoachSample: React.FC = () => (
  <AppShell
    tabs={['Matches', 'Paths', 'Resume', 'Coach']}
    active={3}
    toolbar={
      <>
        <span>Session 7 · Offer decision</span>
        <span className="text-foreground">Profile loaded</span>
      </>
    }
  >
    <div className="space-y-3 text-xs leading-relaxed">
      <div className="flex justify-end">
        <div className="max-w-[85%] rounded-2xl rounded-br-sm border border-border/60 bg-muted/30 p-3 text-foreground">
          I got the offer but something feels off about it.
        </div>
      </div>
      <div className="flex items-start gap-2">
        <span className="mt-0.5 grid h-6 w-6 shrink-0 place-items-center rounded-full bg-info/15 text-[10px] font-medium text-info">
          PC
        </span>
        <div className="max-w-[85%] rounded-2xl rounded-bl-sm border border-info/30 bg-info/5 p-3 text-muted-foreground">
          You have now turned down two roles for the same reason — both had no visible feedback loop. That is a
          boundary, not indecision. Ask them how success gets reviewed in the first ninety days.
        </div>
      </div>
    </div>
    <div className="flex items-center justify-between rounded-xl border border-border/60 px-3 py-2 text-[11px] text-muted-foreground">
      <span>Ask a follow-up…</span>
      <span className="text-foreground">Send</span>
    </div>
  </AppShell>
);


export const CareerPathsSample: React.FC = () => (
  <AppShell
    tabs={['Matches', 'Paths', 'Resume', 'Coach']}
    active={1}
    toolbar={
      <>
        <span>Two-year horizon</span>
        <span className="text-foreground">3 paths compared</span>
      </>
    }
  >
    <div className="rounded-xl border border-border/60 bg-card p-3 space-y-3">
      <div className="flex items-center gap-2">
        <Route className="h-3.5 w-3.5 text-info" aria-hidden />
        <PanelTitle title="Product leadership path" meta="4 steps" />
      </div>
      <div className="relative space-y-3 pl-4">
        <span className="absolute left-[5px] top-1 bottom-1 w-px bg-border" aria-hidden />
        {[
          ['Now', 'Senior PM', 'Owns one surface end to end'],
          ['6–12 mo', 'Lead PM', 'Adds a second team and hiring input'],
          ['1–2 yr', 'Group PM', 'Trades building for judgement calls'],
          ['Stretch', 'Head of Product', 'Only if you want the political load'],
        ].map(([when, role, note]) => (
          <div key={role} className="relative">
            <span className="absolute -left-4 top-1.5 h-2 w-2 rounded-full bg-info" aria-hidden />
            <p className="text-[11px] uppercase tracking-widest text-muted-foreground">{when}</p>
            <p className="text-xs font-medium text-foreground">{role}</p>
            <p className="text-[11px] leading-relaxed text-muted-foreground">{note}</p>
          </div>
        ))}
      </div>
    </div>
    <div className="rounded-xl border border-border/60 bg-muted/20 p-3 space-y-3">
      <PanelTitle title="Decision board" meta="Weighted by what you said matters" />
      <MeterRow label="Northlight — Senior PM" value={86} band="Best fit" />
      <MeterRow label="Halden — Research lead" value={71} band="Workable" />
      <MeterRow label="Stay and negotiate" value={58} band="Fallback" />
    </div>
    <Callout tone="info" label="Why the order:">
      autonomy and feedback loops carry the most weight in your answers, so pay was not the tiebreaker.
    </Callout>
  </AppShell>
);

/* ========================================================================== *
 * Relationships — mirrors the compatibility report header + dimensions
 * ========================================================================== */

export const RelationshipsSample: React.FC = () => (
  <AppShell
    tabs={['Report', 'Dynamics', 'Try this', 'Messages']}
    toolbar={
      <>
        <span>Shared report · both complete</span>
        <span className="text-foreground">Updated today</span>
      </>
    }
  >
    <div className="flex items-center justify-between gap-3 rounded-xl border border-border/60 bg-muted/20 p-3">
      <div className="min-w-0 text-center">
        <p className="text-sm font-medium text-foreground">You</p>
        <p className="text-[11px] text-muted-foreground">ENFJ · 2w3</p>
      </div>
      <FitRing value={78} caption="Overall fit" />
      <div className="min-w-0 text-center">
        <p className="text-sm font-medium text-foreground">Sam</p>
        <p className="text-[11px] text-muted-foreground">INTP · 5w4</p>
      </div>
    </div>
    <div className="space-y-3">
      <PanelTitle title="Dimensions" meta="Band · score" />
      <MeterRow label="Communication" value={82} band="Strong" />
      <MeterRow label="Conflict repair" value={54} band="Needs work" />
      <MeterRow label="Pace and energy" value={71} band="Workable" />
      <MeterRow label="Values overlap" value={88} band="Strong" />
    </div>
    <div className="grid gap-2 sm:grid-cols-2">
      <Callout tone="destructive" label="Where friction comes from">
        You want the repair now; they need to go quiet first. The timing is the argument.
      </Callout>
      <Callout tone="success" label="What genuinely works">
        You bring momentum, they bring the check on it. Joint decisions hold up better.
      </Callout>
    </div>
  </AppShell>
);

export const RelationshipsInviteSample: React.FC = () => (
  <AppShell tabs={['Report', 'Dynamics', 'Try this', 'Messages']} toolbar={<span>Invite pending</span>}>
    <div className="rounded-xl border border-border/60 bg-muted/25 p-3 space-y-2">
      <p className="text-[11px] uppercase tracking-widest text-muted-foreground">One link</p>
      <div className="rounded-lg border border-primary/30 bg-primary/10 px-3 py-2 text-xs text-foreground">
        psyforge.com/invite/8fk2r
      </div>
      <p className="text-xs leading-relaxed text-muted-foreground">
        They answer for themselves. You never see their raw answers — only the shared report.
      </p>
    </div>
    <div className="space-y-2 rounded-xl border border-border/60 p-3">
      <div className="flex items-center justify-between text-xs">
        <span className="text-foreground">Report unlocks when both finish</span>
        <span className="text-success tabular-nums">1 of 2</span>
      </div>
      <div className="h-1.5 overflow-hidden rounded-full bg-muted/50">
        <div className="h-full w-1/2 rounded-full bg-success" />
      </div>
      <div className="flex items-center justify-between text-[11px] text-muted-foreground">
        <span>You · complete</span>
        <span>Sam · invited</span>
      </div>
    </div>
  </AppShell>
);

export const RelationshipsActionsSample: React.FC = () => (
  <AppShell tabs={['Report', 'Dynamics', 'Try this', 'Messages']} active={2} toolbar={<span>Conflict repair · 54</span>}>
    {[
      ['For you', 'Say the repair can wait an hour, out loud. It removes the abandonment reading.'],
      ['For Sam', 'Name the pause instead of taking it silently: "I need an hour."'],
      ['Together', 'Agree the hour in advance, once, while nothing is wrong.'],
    ].map(([who, what]) => (
      <div
        key={who}
        className="rounded-xl border-y border-r border-l-2 border-border/60 border-l-primary/50 bg-muted/20 p-3"
      >
        <p className="text-[11px] uppercase tracking-widest text-muted-foreground">{who}</p>
        <p className="text-xs leading-relaxed text-foreground">{what}</p>
      </div>
    ))}
    <p className="text-[11px] text-muted-foreground">Actions come from your lowest dimension, not a generic list.</p>
  </AppShell>
);


export const RelationshipsMessagesSample: React.FC = () => (
  <AppShell tabs={['Report', 'Dynamics', 'Try this', 'Messages']} active={3} toolbar={<><span>You and Sam</span><span className="text-foreground">Private thread</span></>}>
    <div className="space-y-3 text-xs leading-relaxed">
      <div className="flex items-start gap-2">
        <span className="mt-0.5 grid h-6 w-6 shrink-0 place-items-center rounded-full bg-muted text-[10px] text-muted-foreground">
          S
        </span>
        <div className="max-w-[85%] rounded-2xl rounded-bl-sm border border-border/60 bg-muted/30 p-3 text-foreground">
          Can we talk about Saturday later? I need a minute first.
        </div>
      </div>
      <div className="flex justify-end">
        <div className="max-w-[85%] rounded-2xl rounded-br-sm border border-primary/30 bg-primary/10 p-3 text-foreground">
          Yes — an hour, then we pick it up.
        </div>
      </div>
    </div>
    <div className="rounded-xl border-y border-r border-l-2 border-border/60 border-l-primary/50 bg-muted/20 p-3">
      <p className="text-[11px] uppercase tracking-widest text-muted-foreground">Suggested prompt</p>
      <p className="text-xs leading-relaxed text-foreground">
        Ask what the hour is for. Naming it stops you reading the pause as distance.
      </p>
    </div>
    <p className="text-[11px] text-muted-foreground">
      Prompts come from your own report — the thread itself stays between you two.
    </p>
  </AppShell>
);

/* ========================================================================== *
 * Enterprise — mirrors GroupProfileRadar / PairwiseMatrix / export
 * ========================================================================== */

const TEAM = ['MA', 'DN', 'PR', 'SM', 'JO'];
const PAIR_SCORES: Record<string, number> = {
  'MA-DN': 88,
  'MA-PR': 63,
  'MA-SM': 71,
  'MA-JO': 52,
  'DN-PR': 54,
  'DN-SM': 79,
  'DN-JO': 66,
  'PR-SM': 41,
  'PR-JO': 74,
  'SM-JO': 58,
};

export const EnterpriseSample: React.FC = () => (
  <AppShell
    tabs={['Team', 'Pairs', 'Blind spots', 'Export']}
    toolbar={
      <>
        <span>Product team · 9 members</span>
        <span className="text-foreground">Mean ± 1 SD</span>
      </>
    }
  >
    <div className="rounded-xl border border-border/60 bg-card p-3">
      <PanelTitle title="Team Big Five profile" meta="9 members" />
      <RadarMini
        data={[
          { label: 'Open', value: 78 },
          { label: 'Consc', value: 64 },
          { label: 'Extra', value: 71 },
          { label: 'Agree', value: 58 },
          { label: 'Neuro', value: 42 },
        ]}
      />
    </div>
    <div className="grid grid-cols-4 gap-2">
      {[
        ['Drivers', '4'],
        ['Analysts', '3'],
        ['Connectors', '2'],
        ['Stabilisers', '0'],
      ].map(([k, v]) => (
        <div
          key={k}
          className={cn(
            'rounded-xl border p-2.5 text-center',
            v === '0' ? 'border-warning/40 bg-warning/10' : 'border-border/60 bg-muted/25',
          )}
        >
          <div className="font-display text-lg text-foreground tabular-nums">{v}</div>
          <div className="text-[10px] text-muted-foreground">{k}</div>
        </div>
      ))}
    </div>
    <Callout tone="warning" label="Group blind spot:">
      nobody here naturally slows a decision down. Add the check deliberately.
    </Callout>
  </AppShell>
);

export const EnterprisePairsSample: React.FC = () => (
  <AppShell tabs={['Team', 'Pairs', 'Blind spots', 'Export']} active={1} toolbar={<span>Pairwise compatibility matrix</span>}>
    <div className="rounded-xl border border-border/60 bg-card p-3 space-y-3">
      <div className="grid gap-1" style={{ gridTemplateColumns: `28px repeat(${TEAM.length}, minmax(0,1fr))` }}>
        <span />
        {TEAM.map((t) => (
          <span key={t} className="text-center text-[10px] text-muted-foreground">
            {t}
          </span>
        ))}
        {TEAM.map((row) => (
          <React.Fragment key={row}>
            <span className="flex items-center text-[10px] text-muted-foreground">{row}</span>
            {TEAM.map((col) => {
              if (row === col) {
                return <span key={col} className="aspect-square rounded-sm border border-border/50 bg-muted/30" />;
              }
              const score = PAIR_SCORES[`${row}-${col}`] ?? PAIR_SCORES[`${col}-${row}`];
              const cell =
                score >= 80
                  ? 'bg-success/45 text-success'
                  : score >= 60
                    ? 'bg-warning/30 text-warning'
                    : 'bg-destructive/25 text-destructive';
              return (
                <span
                  key={col}
                  className={cn('grid aspect-square place-items-center rounded-sm text-[9px] tabular-nums', cell)}
                >
                  {score}
                </span>
              );
            })}

          </React.Fragment>
        ))}
      </div>
      <div className="flex items-center gap-3 text-[10px] text-muted-foreground">
        <span className="inline-flex items-center gap-1">
          <span className="h-2 w-2 rounded-sm bg-success" />
          Runs itself
        </span>
        <span className="inline-flex items-center gap-1">
          <span className="h-2 w-2 rounded-sm bg-warning/70" />
          Needs structure
        </span>
        <span className="inline-flex items-center gap-1">
          <span className="h-2 w-2 rounded-sm bg-destructive/60" />
          Pair with a third
        </span>
      </div>
    </div>
    <div className="space-y-2">
      <MeterRow label="Maya ↔ Dan" value={88} />
      <MeterRow label="Dan ↔ Priya" value={54} />
      <MeterRow label="Priya ↔ Sam" value={41} />
    </div>
  </AppShell>
);

export const EnterpriseExportSample: React.FC = () => (
  <AppShell tabs={['Team', 'Pairs', 'Blind spots', 'Export']} active={3} toolbar={<span>Leadership summary · PDF</span>}>
    <div className="rounded-xl border border-border/60 bg-card p-3 space-y-3">
      <div className="flex items-baseline justify-between gap-3 border-b border-border/60 pb-2">
        <div>
          <p className="font-display text-sm text-foreground">Product team — composition review</p>
          <p className="text-[10px] text-muted-foreground">
            Northlight Labs · 9 members · window 1 Jul – 6 Sep · generated 6 Sep
          </p>
        </div>
        <span className="text-[10px] uppercase tracking-widest text-muted-foreground">Confidential</span>
      </div>
      <div className="space-y-2 text-xs">
        {[
          ['Team size', '9'],
          ['Coverage gaps', '2'],
          ['Pairs needing structure', '3'],
          ['Decision speed risk', 'High'],
        ].map(([k, v]) => (
          <div key={k} className="flex items-center justify-between rounded-lg border border-border/60 bg-muted/20 px-3 py-2">
            <span className="text-muted-foreground">{k}</span>
            <span className="font-display text-base text-foreground tabular-nums">{v}</span>
          </div>
        ))}
      </div>
    </div>
    <p className="text-[11px] text-muted-foreground">Written for decisions, not for a psychology seminar.</p>
  </AppShell>
);

/* ========================================================================== *
 * Liker — mirrors LikerCardStack / deck mix / insights
 * ========================================================================== */

export const LikerSample: React.FC = () => (
  <AppShell
    tabs={['Discover', 'Liked', 'Collections', 'Insights']}
    toolbar={
      <>
        <span>Film · tonight</span>
        <span className="text-foreground">Streak 6</span>
      </>
    }
  >
    <PosterCard
      title="Arrival"
      meta="Film · 2016 · Sci-fi, drama"
      score={92}
      arm="More like this"
      tags={['Slow build', 'Cerebral', 'Melancholy']}
      why="you keep choosing stories that solve a problem through understanding rather than force, and you tolerate a slow first act if the payoff reframes it."
    />
    <div className="flex items-center justify-center gap-2 text-[11px] text-muted-foreground">
      <span className="rounded-full border border-border/60 px-2.5 py-1">Pass</span>
      <span className="rounded-full border border-info/40 px-2.5 py-1 text-info">Interested</span>
      <span className="rounded-full border border-success/40 px-2.5 py-1 text-success">Love it</span>
    </div>
    <div className="space-y-2 rounded-xl border border-border/60 bg-muted/20 p-3">
      <PanelTitle title="Goes into" meta="Collections build as you swipe" />
      <div className="flex gap-2 overflow-hidden">
        {['Patient sci-fi', 'Watch with Sam', 'Sunday nights'].map((c) => (
          <div key={c} className="flex min-w-0 flex-1 items-center gap-2 rounded-lg border border-border/60 bg-card px-2 py-1.5">
            <span className="h-6 w-4 shrink-0 rounded-sm bg-gradient-to-br from-success/40 to-muted" aria-hidden />
            <span className="truncate text-[11px] text-muted-foreground">{c}</span>
          </div>
        ))}
      </div>
    </div>
  </AppShell>
);

export const LikerSetSample: React.FC = () => (
  <AppShell tabs={['Discover', 'Liked', 'Collections', 'Insights']} toolbar={<span>Deck mix · every three cards</span>}>
    {[
      ['More like this', 'Patient sci-fi that rewards attention', 92, Repeat, 'text-success border-success/30 bg-success/15'],
      ['Adjacent', 'Same patience, different genre', 77, Compass, 'text-info border-info/30 bg-info/15'],
      ['A stretch', 'Deliberately outside your pattern', 58, Rocket, 'text-accent border-accent/30 bg-accent/15'],
    ].map(([kind, why, score, Icon, pill]) => {
      const K = Icon as React.ElementType;
      return (
        <div key={kind as string} className="flex items-center gap-3 rounded-xl border border-border/60 bg-card p-3">
          <div className="h-14 w-10 shrink-0 rounded-md bg-gradient-to-br from-success/30 to-muted" aria-hidden />
          <div className="min-w-0 space-y-1">
            <span
              className={cn('inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px]', pill as string)}
            >
              <K className="h-3 w-3" aria-hidden />
              {kind as string}
            </span>
            <p className="text-[11px] leading-relaxed text-muted-foreground">{why as string}</p>
          </div>
          <span className="ml-auto text-[11px] tabular-nums text-muted-foreground">{score as number}%</span>
        </div>
      );
    })}
  </AppShell>
);

export const LikerTasteSample: React.FC = () => (
  <AppShell tabs={['Discover', 'Liked', 'Collections', 'Insights']} active={3} toolbar={<span>From 84 swipes</span>}>
    <div className="rounded-xl border border-border/60 bg-card p-3 space-y-3">
      <PanelTitle title="Your taste, read back" meta="Confidence: high" />
      <MeterRow label="Character over plot" value={86} tone="bg-success" />
      <MeterRow label="Slow builds" value={74} tone="bg-success/80" />
      <MeterRow label="Spectacle" value={28} tone="bg-success/40" />
    </div>
    <Callout tone="success">
      Your passes cluster on spectacle without stakes. That is a preference, not a gap.
    </Callout>
  </AppShell>
);


/* ========================================================================== *
 * Astrology — mirrors ChartWheel / BigThreeCard / AIPanel / SynastryPanel /
 * TransitForecast. The wheel is the real component fed by one fixed sample
 * birth, computed once at module scope: deterministic, no fetching, no state.
 * ========================================================================== */

const ASTRO_TABS = ['Chart', 'Placements', 'Reading', 'Synastry', 'Transits'];

const SAMPLE_CHART = computeChart({
  name: 'Sample chart',
  year: 1990,
  month: 6,
  day: 14,
  hour: 8,
  minute: 32,
  tzOffset: -7,
  lat: 34.05,
  lon: -118.24,
  placeName: 'Los Angeles, CA',
  houseSystem: 'placidus',
});

const bigThree = (() => {
  const rows: { label: string; sign: string; degree: number; house: number | null }[] = [];
  const sun = SAMPLE_CHART.placements.Sun;
  const moon = SAMPLE_CHART.placements.Moon;
  if (sun) {
    const s = signOf(sun.longitude);
    rows.push({ label: 'Sun', sign: s.sign, degree: s.degree, house: sun.house ?? null });
  }
  if (moon) {
    const s = signOf(moon.longitude);
    rows.push({ label: 'Moon', sign: s.sign, degree: s.degree, house: moon.house ?? null });
  }
  const asc = signOf(SAMPLE_CHART.asc);
  rows.push({ label: 'Rising', sign: asc.sign, degree: asc.degree, house: 1 });
  return rows;
})();

export const AstrologyChartSample: React.FC = () => (
  <AppShell
    tabs={ASTRO_TABS}
    toolbar={
      <>
        <span>Los Angeles · 14 Jun 1990, 08:32</span>
        <span className="text-foreground">Placidus houses</span>
      </>
    }
  >
    <div className="rounded-xl border border-border/60 bg-card p-2">
      <ChartWheel chart={SAMPLE_CHART} size={300} />
    </div>
    <div className="grid gap-2 sm:grid-cols-3">
      {bigThree.map((r) => {
        const meaning = SIGN_MEANINGS[r.sign];
        return (
          <div key={r.label} className="rounded-xl border border-border/60 bg-muted/20 p-3 space-y-1">
            <p className="text-[11px] uppercase tracking-widest text-muted-foreground">{r.label}</p>
            <p className="text-sm font-medium text-foreground">
              {r.sign} {Math.floor(r.degree)}°
              {r.house ? <span className="text-muted-foreground"> · house {r.house}</span> : null}
            </p>
            <p className="text-[11px] leading-relaxed text-muted-foreground">
              {meaning ? `${meaning.keyword} — ${meaning.traits}` : 'Plain-language reading included.'}
            </p>
          </div>
        );
      })}
    </div>
    <p className="text-[11px] text-muted-foreground">
      Real chart maths — houses, aspects and retrogrades — not a decorative graphic.
    </p>
  </AppShell>
);

export const AstrologyReadingSample: React.FC = () => (
  <AppShell tabs={ASTRO_TABS} active={2} toolbar={<span>Natal reading · saved to your chart</span>}>
    <div className="flex items-center gap-1 rounded-lg bg-muted/30 p-1 text-[11px]">
      {['Brief', 'Standard', 'Deep'].map((d, i) => (
        <span
          key={d}
          className={cn(
            'rounded-md px-2.5 py-1',
            i === 1 ? 'bg-card text-foreground shadow-sm' : 'text-muted-foreground',
          )}
        >
          {d}
        </span>
      ))}
    </div>
    <div className="flex flex-wrap gap-1.5">
      {bigThree.map((r) => (
        <Badge key={r.label} variant="outline" className="border-primary/40 text-primary text-[10px]">
          {r.label} in {r.sign}
        </Badge>
      ))}
      <Badge variant="outline" className="border-border/60 text-muted-foreground text-[10px]">
        + 8 more placements
      </Badge>
    </div>
    <div className="rounded-xl border border-border/60 bg-card p-3 space-y-3">
      <div className="flex items-center gap-2">
        <Sparkles className="h-3.5 w-3.5 text-primary" aria-hidden />
        <PanelTitle title="How you are built" meta="Standard depth" />
      </div>
      <p className="text-xs leading-relaxed text-muted-foreground">
        Your chart puts the talking, testing and sorting functions right at the front. You process by
        putting things into words, which is why an unspoken problem stays heavier for you than a
        spoken one — even a badly spoken one.
      </p>
      <p className="text-[11px] uppercase tracking-widest text-muted-foreground">The tension</p>
      <p className="text-xs leading-relaxed text-muted-foreground">
        The same chart wants certainty from a placement that runs on movement. You will keep looking
        for a final answer in the one area designed to keep changing its mind.
      </p>
    </div>
    <Callout tone="primary" label="Reading, not prediction:">
      every paragraph names the placement it came from, so you can disagree with it.
    </Callout>
  </AppShell>
);

export const AstrologySynastrySample: React.FC = () => (
  <AppShell tabs={ASTRO_TABS} active={3} toolbar={<span>Synastry · saved report</span>}>
    <div className="flex items-center justify-between gap-3 rounded-xl border border-border/60 bg-muted/20 p-3">
      <div className="min-w-0 text-center">
        <p className="text-sm font-medium text-foreground">You</p>
        <p className="text-[11px] text-muted-foreground">Gemini · Scorpio rising</p>
      </div>
      <FitRing value={73} caption="Contact strength" />
      <div className="min-w-0 text-center">
        <p className="text-sm font-medium text-foreground">Sam</p>
        <p className="text-[11px] text-muted-foreground">Aquarius · Leo rising</p>
      </div>
    </div>
    <div className="space-y-2 rounded-xl border border-border/60 bg-card p-3">
      <PanelTitle title="Strongest contacts" meta="Aspect · orb" />
      {[
        ['Your Venus', 'their Mars', 'trine', '1.2°'],
        ['Your Moon', 'their Saturn', 'square', '0.8°'],
        ['Your Mercury', 'their Mercury', 'conjunction', '2.4°'],
      ].map(([a, b, asp, orb]) => (
        <div key={a} className="flex items-baseline justify-between gap-3 border-b border-border/50 py-1 text-xs last:border-0">
          <span className="text-foreground">
            {a} <span className="text-muted-foreground">to</span> {b}
          </span>
          <span className="text-muted-foreground tabular-nums">
            {asp}, orb {orb}
          </span>
        </div>
      ))}
    </div>
    <div className="grid gap-2 sm:grid-cols-2">
      <Callout tone="success" label="Runs easily">
        Attraction and pace agree. Plans you make together tend to actually happen.
      </Callout>
      <Callout tone="destructive" label="Costs effort">
        Their caution reads as withdrawal to you. Same event, two different stories.
      </Callout>
    </div>
  </AppShell>
);

export const AstrologyTransitSample: React.FC = () => (
  <AppShell tabs={ASTRO_TABS} active={4} toolbar={<><span>Today&apos;s sky</span><span className="text-foreground">Within 3°</span></>}>
    <div className="rounded-xl border border-border/60 bg-card p-3 space-y-2">
      <div className="flex items-center gap-2">
        <MoonStar className="h-3.5 w-3.5 text-primary" aria-hidden />
        <PanelTitle title="Active transits to your chart" />
      </div>
      {[
        ['Jupiter', 'natal Sun', 'trine', '0.6°', 'A door opens if you ask for it out loud.'],
        ['Saturn', 'natal Moon', 'square', '1.4°', 'Low tolerance for other people this week — plan lighter.'],
        ['Mars', 'natal Mercury', 'conjunction', '2.1°', 'Sharper than you intend in writing. Reread before sending.'],
      ].map(([tp, np, asp, orb, note]) => (
        <div key={tp} className="space-y-1 border-b border-border/50 pb-2 text-xs last:border-0 last:pb-0">
          <div className="flex items-baseline justify-between gap-3">
            <span className="text-foreground">
              Transit <span className="font-medium">{tp}</span> to {np}
            </span>
            <span className="text-muted-foreground tabular-nums">
              {asp}, orb {orb}
            </span>
          </div>
          <p className="leading-relaxed text-muted-foreground">{note}</p>
        </div>
      ))}
    </div>
    <p className="text-[11px] text-muted-foreground">Windows, not fortunes — each one names its own orb.</p>
  </AppShell>
);

/* ========================================================================== */

export const TOOL_SAMPLES: Record<ToolSlug, React.FC> = {
  career: CareerSample,
  relationships: RelationshipsSample,
  enterprise: EnterpriseSample,
  liker: LikerSample,
  astrology: AstrologyChartSample,
};

export interface ToolSamplePanel {
  label: string;
  Component: React.FC;
}

/** Two or three labelled example panels per tool, shown as a strip. */
export const TOOL_SAMPLE_SET: Record<ToolSlug, ToolSamplePanel[]> = {
  career: [
    { label: 'Role matches', Component: CareerSample },
    { label: 'Paths and decisions', Component: CareerPathsSample },
    { label: 'Tailored resume', Component: CareerResumeSample },
    { label: 'Coach reply', Component: CareerCoachSample },
  ],
  relationships: [
    { label: 'The pairing', Component: RelationshipsSample },
    { label: 'The invite', Component: RelationshipsInviteSample },
    { label: 'What to try', Component: RelationshipsActionsSample },
    { label: 'The conversation', Component: RelationshipsMessagesSample },
  ],
  enterprise: [
    { label: 'Composition', Component: EnterpriseSample },
    { label: 'Working pairs', Component: EnterprisePairsSample },
    { label: 'Leadership export', Component: EnterpriseExportSample },
  ],
  liker: [
    { label: "Tonight's pick", Component: LikerSample },
    { label: 'Deck mix', Component: LikerSetSample },
    { label: 'Taste read-back', Component: LikerTasteSample },
  ],
  astrology: [
    { label: 'Your chart', Component: AstrologyChartSample },
    { label: 'The reading', Component: AstrologyReadingSample },
    { label: 'You two together', Component: AstrologySynastrySample },
    { label: "What's moving now", Component: AstrologyTransitSample },
  ],
};

/**
 * Optional hands-on section: the one guest surface that is genuinely
 * interactive. Shown further down the page, after the examples.
 */
export const TOOL_TRY_IT: Partial<Record<ToolSlug, { title: string; blurb: string; Component: React.FC }>> = {
  astrology: {
    title: 'Cast a chart right now',
    blurb: 'No account needed for the wheel and placements. Readings, synastry and transits need a free account.',
    Component: AstrologyPreview as React.FC,
  },
};
